import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@heroicons/react/24/outline';
import { CellContext } from '@tanstack/react-table';

import { logError } from 'services/logging/logging';
import { GenericError } from 'support/helpers/errors/errors';
import { Button } from 'components/Form/Button';

import { useDataTableContext } from '../../../contexts/DataTableContext';
import { generateEmbeddedActionTestId } from '../../../utils/generateEmbeddedActionTestId';

export type Props<TData, TValue> = CellContext<TData, TValue>;

export const AddEmbeddedRowCell = <TData, TValue>({ cell, row, column, table }: Props<TData, TValue>): JSX.Element => {
  const { t } = useTranslation();
  const { addEmbedded } = useDataTableContext();
  const isSubmitting = table.options.meta?.isSubmitting ?? false;

  const buttonLabel = column.parent?.columnDef.meta?.addRowButtonLabel ?? t('common:dataTable.addRow');

  useEffect(() => {
    if (!column.parent?.columnDef.meta?.addRowButtonLabel) {
      logError(
        new GenericError('AddEmbeddedRowCell: Missing addRowButtonLabel in column meta.', { columnId: column.id }),
      );
    }
  }, [column]);
  const testId = useMemo(() => {
    return generateEmbeddedActionTestId({
      dataPrefix: table.options.meta?.dataPrefix,
      rowIndex: row.index,
      parentColumnPath: column.parent?.id,
      suffix: 'addEmbeddedRowCell',
    });
  }, [table.options.meta?.dataPrefix, column.parent?.id, row.index]);

  const onClickAdd = useCallback(() => {
    const parentColumnPath = column.parent?.id;

    if (parentColumnPath) {
      addEmbedded(row.index, parentColumnPath);
    }
  }, [addEmbedded, row.index, column.parent?.id]);

  return (
    <div className="flex size-full items-center justify-center">
      <div className="flex size-full justify-center">
        <Button
          size="extra-small"
          variant="minimal"
          RightIcon={PlusIcon}
          onClick={onClickAdd}
          analyticsId={`table_embedded_row_add_${cell.id}`}
          testId={testId}
          className="!size-full justify-center"
          disabled={isSubmitting}
        >
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
};
